import { Component, Input, Output, EventEmitter, ContentChild, TemplateRef, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Base } from '../../models/base.model';
import { ResultSet } from '../../models/result-set.model';
import { ColumnConfig, InputMode } from '../../models/column-config.model';
import { DatatableActionsConfig } from './models/data-table-actions-config.model';


@Component({
  selector: 'app-data-table-layout',
  templateUrl: './data-table-layout.component.html',
  styleUrl: './data-table-layout.component.scss',
  standalone: false
})
export class DataTableLayoutComponent<T extends Base> implements OnChanges {

  /** Table Data and Layout Inputs **/


  @Input() columns: ColumnConfig[] = [];
  @Input() resultSet: ResultSet<T> | null = null;
  @Input() editForms: { [key: string]: FormGroup } = {};
  @Input() editItemID: number | null = null;
  @Input() generateLink: (routeTemplate: string[], item: T, column: ColumnConfig) => string | any[] = () => '#' ;
  @Input() getDisplayValue: (item: T, column: ColumnConfig) => any = (item, column) => this.editForms[item.id].get(column.name)?.value ;
  @Input() edgePageCount = 5;
  @Input() siblingPageCount = 3;

  /** Pagination Inputs **/
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 0;
  visiblePages: (number | '...')[] = [];

  /** Header **/
  @Input() currentValidationErrors: { columnLabel: string; message: string }[] = []

  @ContentChild('rowActions', { read: TemplateRef, static: false })
  rowActionsTemplate?: TemplateRef<{ $implicit: T }>;

  @ContentChild('toolbarMiddle', { read: TemplateRef, static: false })
  toolbarMiddleTemplate?: TemplateRef<unknown>;

  /** Button Visibility **/
  private _actionsConfig: DatatableActionsConfig = {
    showSearch: false,
    showBackButton: false,
    showCreate: false,
    showEdit: false,
    showDelete: false,
    showDownload: true,
  };

  @Input()
  set actionsConfig(config: DatatableActionsConfig) {
    this._actionsConfig = { ...this._actionsConfig, ...config };
  }

  get actionsConfig(): DatatableActionsConfig {
    return this._actionsConfig;
  }

  get spacerColumns(): number[] {
    const amount = 10 - this.columns.length;
    return Array(amount);
  }

  get data(): T[] {
    return this.resultSet?.data?.filter(item => item.id != null) ?? [];
  }

  searchTerm: string = '';

  /** Output Events **/
  @Output() editStart = new EventEmitter<number>();
  @Output() deleteStart = new EventEmitter<number>();
  @Output() editSave = new EventEmitter<void>();
  @Output() editCancel = new EventEmitter<void>();
  @Output() currentPageChange = new EventEmitter<number>();
  @Output() goToFirstPage = new EventEmitter<void>();
  @Output() goToPrevPage = new EventEmitter<void>();
  @Output() goToNextPage = new EventEmitter<void>();
  @Output() goToLastPage = new EventEmitter<void>();
  @Output() createStart = new EventEmitter<void>();
  @Output() CSVDownload = new EventEmitter<void>();
  @Output() goBack = new EventEmitter<void>();
  @Output() search = new EventEmitter<string>();

  /** Event Emiters for Binding **/

  startEdit(id: number) {
    this.editStart.emit(id);
  }

  startDelete(id: number) {
    this.deleteStart.emit(id);
  }

  saveEdit() {
    this.editSave.emit();
  }

  cancelEdit() {
    this.editCancel.emit();
  }

  onCurrentPageChange(page: number) {
    this.currentPageChange.emit(page);
  }

  onSearch() {
    this.search.emit(this.searchTerm);
  }

  firstPage() {
    this.goToFirstPage.emit();
  }

  prevPage() {
    this.goToPrevPage.emit();
  }

  nextPage() {
    this.goToNextPage.emit();
  }

  lastPage() {
    this.goToLastPage.emit();
  }

  downloadCSV() {
    this.CSVDownload.emit();
  }
  startCreate() {
    this.createStart.emit();
  }
  back() {
    this.goBack.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['currentPage'] ||
      changes['totalPages'] ||
      changes['edgePageCount'] ||
      changes['siblingPageCount']
    ) {
      this.visiblePages = this.getVisiblePages(
        this.currentPage,
        this.totalPages,
        this.edgePageCount,
        this.siblingPageCount
      );
    }
  }

  getVisiblePages(
    currentPage: number,
    totalPages: number,
    edgePageCount: number,
    siblingPageCount: number
  ): (number | '...')[] {
    const pages = new Set<number>();

    for (let i = 1; i <= Math.min(edgePageCount, totalPages); i++) {
      pages.add(i);
    }

    for (
      let i = Math.max(totalPages - edgePageCount + 1, 1);
      i <= totalPages;
      i++
    ) {
      pages.add(i);
    }

    for (
      let i = Math.max(currentPage - siblingPageCount, 1);
      i <= Math.min(currentPage + siblingPageCount, totalPages);
      i++
    ) {
      pages.add(i);
    }

    const sorted = [...pages].sort((a, b) => a - b);
    const result: (number | '...')[] = [];

    for (let i = 0; i < sorted.length; i++) {
      const page = sorted[i];
      const prev = sorted[i - 1];

      if (i > 0) {
        const gap = page - prev;

        if (gap === 2) {
          result.push(prev + 1);
        } else if (gap > 2) {
          result.push('...');
        }
      }

      result.push(page);
    }

    return result;
  }
}
